import { useCallback } from 'react';
import { useComputedColorScheme, useMantineColorScheme } from '@mantine/core';

export type ColorSchemeValue = 'light' | 'dark';

interface UseColorSchemeOptions {
  /**
   * When true (default) the hook will read local storage outside of effects so the
   * initial render reflects the persisted choice immediately.
   */
  getInitialValueInEffect?: boolean;
}

export function useColorScheme(options: UseColorSchemeOptions = {}) {
  const { getInitialValueInEffect = false } = options;
  const computedColorScheme = useComputedColorScheme('light', { getInitialValueInEffect });
  const { setColorScheme, clearColorScheme } = useMantineColorScheme();

  const toggleColorScheme = useCallback(() => {
    setColorScheme(computedColorScheme === 'dark' ? 'light' : 'dark');
  }, [computedColorScheme, setColorScheme]);

  const applyColorScheme = useCallback(
    (scheme: ColorSchemeValue) => {
      setColorScheme(scheme);
    },
    [setColorScheme]
  );

  return {
    colorScheme: computedColorScheme as ColorSchemeValue,
    isDark: computedColorScheme === 'dark',
    isLight: computedColorScheme === 'light',
    toggleColorScheme,
    setColorScheme: applyColorScheme,
    clearColorScheme,
  };
}
